// ─────────────────────────────────────────────────────────────────────────────
// Plan Management — REVIEW QUEUES. Two operator work lists behind one screen:
//
//   Intake        (spec 5.4) — the Received holding queue. Anything the system
//                 could not process on its own waits here WITH ITS FULL TRAIL,
//                 because section 1 says nothing is silently dropped.
//   Notifications (spec 13)  — the task queue. Every row owns a record, an
//                 audience and a response target, and is closed by resolving it.
//
// Both are scan → open → act → next, so both use the Workbench split pane: the
// list never moves out from under the operator. No modals — a modal costs the
// queue position, which is the whole reason the redesign exists.
//
// Colour is reserved for STATE: rose = overdue / identity risk, amber = due
// today / needs checking, emerald = within target / matched, slate = neutral.
// Primary (indigo) means selection and nothing else.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  AlertTriangle, Bell, Check, CheckCircle2, Clock, ExternalLink, FileSearch, FileText,
  Inbox, Link2, Mail, Paperclip, RefreshCw, ShieldAlert, User, UserCheck,
} from 'lucide-react';
import api from '../../utils/api';
import {
  BlockReasonList, BulkBar, CopyButton, DataTable, EmptyState, ErrorState, FilterChips,
  MicroLabel, SearchInput, TableSkeleton, Timeline, Toolbar, Workbench, WorkbenchDetail,
  WorkbenchList, fmtDate, fmtDateTime, money,
} from './ui';

const PM = '/plan-management';

// The API's own message is the useful one — the role gate, the feature gate and RLS each say
// something different, and "Something went wrong" hides which of them stopped the operator.
const apiMessage = (e, fallback) => (e && e.response && e.response.data && e.response.data.message) || fallback;
const isForbidden = (e) => !!(e && e.response && e.response.status === 403);

const humanize = (s) => String(s || '').replace(/[_-]+/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

// FilterChips treats an option valued 'all' (or null) as the RESET, and hands that value straight
// back on click. Every single-select chip group here therefore folds it to null, which is also the
// value the "All" chip highlights on — without this the reset chip both loses its highlight and
// leaks the literal string 'all' into a filter.
const ALL = 'all';
const noneIfAll = (v) => (v === ALL || v === '' ? null : v);

const fmtBytes = (n) => {
  const b = Number(n);
  if (!Number.isFinite(b) || b <= 0) return '—';
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1048576).toFixed(1)} MB`;
};

// ── shared fragments ─────────────────────────────────────────────────────────

// A 403 is a permission answer, not a failure — show what the API said and offer no retry,
// because retrying the same call cannot change the answer.
function AccessDenied({ message }) {
  return (
    <EmptyState
      icon={ShieldAlert}
      title="You do not have access to this queue"
      hint={message || 'Ask an administrator to grant you plan management access.'}
    />
  );
}

function Section({ title, right, children, className }) {
  return (
    <section className={clsx('px-5 py-4 border-t border-slate-100', className)}>
      <div className="flex items-center gap-2 mb-3">
        <MicroLabel as="h3">{title}</MicroLabel>
        {right && <div className="ml-auto">{right}</div>}
      </div>
      {children}
    </section>
  );
}

function Meta({ label, value, mono, copy, className }) {
  const empty = value == null || value === '';
  return (
    <div className={clsx('min-w-0', className)}>
      <MicroLabel as="dt">{label}</MicroLabel>
      <dd className="mt-0.5 flex items-start gap-1 min-w-0">
        {empty
          ? <span className="text-[13px] text-slate-300">—</span>
          : <span className={clsx('min-w-0', mono ? 'font-mono text-[11.5px] text-slate-700 break-all' : 'text-[13px] text-slate-800 truncate')}>{value}</span>}
        {!empty && copy ? <CopyButton value={String(value)} size={11} className="shrink-0" /> : null}
      </dd>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════════════════
   TAB 1 — INTAKE (spec 5.1–5.4)
   ═════════════════════════════════════════════════════════════════════════════ */

// Human wording for the hold reasons the matcher can raise, plus the next step that clears each
// one. Tone is risk, not severity: rose where acting on a guess could pay the WRONG participant's
// plan, amber where the item is merely incomplete.
const HOLD_REASONS = {
  not_in_system: {
    label: 'Participant not in system', tone: 'amber',
    nextStep: 'Create the participant profile, then rescan — or mark this as not an invoice.',
  },
  ambiguous: {
    label: 'Same name — needs NDIS number', tone: 'rose',
    nextStep: 'Confirm the NDIS number with the provider or the participant, then link the right profile.',
  },
  name_mismatch: {
    label: 'Name does not match the NDIS number', tone: 'rose',
    nextStep: 'Check the invoice against the participant record before linking. Do not assume the number is the correct one.',
  },
  low_confidence: {
    label: 'Extraction below confidence threshold', tone: 'amber',
    nextStep: 'Read the flagged fields off the attachment and correct them before this becomes an invoice.',
  },
  no_participant: {
    label: 'No participant named', tone: 'amber',
    nextStep: 'Ask the provider to reissue with the participant name and NDIS number, or link the participant by hand.',
  },
  inactive_participant: {
    label: 'Participant is not active', tone: 'rose',
    nextStep: 'Confirm whether plan management still applies. If it does, reactivate the participant, then rescan.',
  },
  after_relationship_end: {
    label: 'Service date after the relationship ended', tone: 'rose',
    nextStep: 'Check the service dates with the provider — this may belong to the incoming plan manager.',
  },
};
const holdInfo = (r) => HOLD_REASONS[r] || { label: humanize(r), tone: 'slate', nextStep: null };

const INTAKE_STATES = [
  { value: 'received', label: 'Held for review' },
  { value: 'processed', label: 'Processed' },
  { value: 'not_an_invoice', label: 'Not an invoice' },
  { value: 'dismissed', label: 'Dismissed' },
];

// Sentinel for "in the received queue but nothing is holding it" — the API counts reasons, not
// their absence, so this chip is derived on the client.
const READY = '__ready__';

// Critical fields first (spec 5.2 lists these verbatim); everything else the extractor found
// follows in alphabetical order so the pane is stable between items.
const EXTRACT_ORDER = ['participant', 'ndis_number', 'abn', 'provider_name', 'invoice_number',
  'invoice_date', 'due_date', 'service_date_from', 'service_date_to', 'total', 'gst', 'line_amount'];
const MONEY_FIELDS = new Set(['total', 'gst', 'line_amount', 'amount', 'subtotal', 'unit_price', 'balance_due']);

function extractionRows(extraction, confidence, low) {
  const src = extraction && typeof extraction === 'object' ? extraction : {};
  const conf = confidence && typeof confidence === 'object' ? confidence : {};
  const lowSet = new Set(Array.isArray(low) ? low : []);
  const keys = Object.keys(src);
  const rank = (k) => { const i = EXTRACT_ORDER.indexOf(k); return i === -1 ? EXTRACT_ORDER.length : i; };
  keys.sort((a, b) => (rank(a) - rank(b)) || a.localeCompare(b));
  // A field can be flagged low-confidence even when the extractor returned nothing for it, so the
  // flagged set is unioned in rather than filtered against what came back.
  for (const k of lowSet) if (!keys.includes(k)) keys.push(k);
  return keys.map((k) => {
    const raw = src[k];
    const isMoney = MONEY_FIELDS.has(k);
    let value;
    if (raw == null || raw === '') value = null;
    else if (Array.isArray(raw)) value = `${raw.length} ${raw.length === 1 ? 'item' : 'items'}`;
    else if (typeof raw === 'object') value = JSON.stringify(raw).slice(0, 120);
    else value = isMoney ? money(raw) : String(raw);
    return { key: k, value, isMoney, low: lowSet.has(k), confidence: conf[k] == null ? null : Number(conf[k]) };
  });
}

function ExtractedField({ field }) {
  return (
    <div className={clsx('rounded-lg border px-3 py-2 min-w-0',
      field.low ? 'border-amber-300 bg-amber-50/60' : 'border-slate-200 bg-white')}>
      <div className="flex items-center gap-1.5">
        <MicroLabel className={field.low ? '!text-amber-700' : undefined}>{humanize(field.key)}</MicroLabel>
        {field.confidence != null && (
          <span className={clsx('ml-auto text-[10px] font-semibold tabular-nums shrink-0',
            field.low ? 'text-amber-700' : 'text-slate-300')}>
            {Math.round(field.confidence * 100)}%
          </span>
        )}
      </div>
      <div className={clsx('mt-0.5 text-[13px] break-words', field.isMoney && 'tabular-nums text-right',
        field.low ? 'font-semibold text-amber-900' : 'text-slate-800')}>
        {field.value == null ? <span className="text-slate-300">Not found</span> : field.value}
      </div>
      {field.low && (
        <p className="mt-1 flex items-start gap-1 text-[10.5px] font-medium text-amber-700">
          <AlertTriangle size={10} className="mt-px shrink-0" />Low confidence — check against the attachment
        </p>
      )}
    </div>
  );
}

// Suggestions are the point of rule 4: a near match NEVER auto-confirms, because auto-confirming
// is how an invoice gets paid out of the wrong person's plan. The panel says so, and every one of
// them needs a click from a person.
function SuggestionList({ suggestions = [], onLink, busy }) {
  if (!suggestions.length) return null;
  return (
    <div className="rounded-xl border border-amber-200 bg-white overflow-hidden">
      <div className="flex items-start gap-2 border-b border-amber-100 bg-amber-50 px-4 py-2.5">
        <AlertTriangle size={14} className="mt-0.5 shrink-0 text-amber-600" />
        <div className="min-w-0">
          <p className="text-[12.5px] font-semibold text-amber-700">
            {suggestions.length} {suggestions.length === 1 ? 'suggestion' : 'suggestions'} — not matches
          </p>
          <p className="mt-0.5 text-[12px] text-slate-600">
            The system will not choose for you. Confirm the participant against the invoice before linking.
          </p>
        </div>
      </div>
      <ul className="divide-y divide-slate-100">
        {suggestions.filter(Boolean).map((s, i) => (
          <li key={s.client_id || i} className="flex items-start gap-3 px-4 py-3">
            <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-400">
              <User size={14} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[13px] font-semibold text-slate-800">{s.name || 'Unnamed participant'}</p>
              <p className="mt-0.5 flex items-center gap-1 text-[11.5px] text-slate-500">
                <span className="tabular-nums">{s.ndis_number || 'No NDIS number on file'}</span>
                {s.ndis_number && <CopyButton value={s.ndis_number} size={10} />}
              </p>
              {s.why && (
                <p className="mt-1 text-[11.5px] text-slate-600">
                  <span className="font-semibold text-slate-500">Why suggested</span> — {s.why}
                </p>
              )}
            </div>
            <button
              type="button" className="btn-secondary btn-sm shrink-0" disabled={busy || !s.client_id}
              onClick={() => onLink && onLink(s.client_id, s.name)}>
              <Link2 size={12} />Link this participant
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Manual link for when no suggestion fits. PM participants only — the module keeps its own
// participant list, never /clients.
function ParticipantPicker({ onPick, busy }) {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState(null);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState('');

  useEffect(() => {
    if (!open || list || err) return;
    let alive = true;
    api.get(`${PM}/participants`)
      .then((r) => { if (alive) setList(Array.isArray(r.data) ? r.data : []); })
      .catch((e) => { if (alive) setErr(apiMessage(e, 'Could not load participants.')); });
    return () => { alive = false; };
  }, [open, list, err]);

  const matches = useMemo(() => {
    const rows = list || [];
    const needle = q.trim().toLowerCase();
    if (!needle) return rows.slice(0, 25);
    return rows.filter((c) => `${c.name || ''} ${c.ndis_number || ''}`.toLowerCase().includes(needle)).slice(0, 25);
  }, [list, q]);

  if (!open) {
    return (
      <button type="button" className="btn-secondary btn-sm" onClick={() => setOpen(true)}>
        <UserCheck size={12} />Link another participant
      </button>
    );
  }
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <div className="mb-2 flex items-center gap-2">
        <MicroLabel>Choose the participant</MicroLabel>
        <button type="button" className="btn-ghost btn-sm ml-auto" onClick={() => setOpen(false)}>Cancel</button>
      </div>
      <SearchInput value={q} onChange={setQ} placeholder="Name or NDIS number" size="sm" />
      <div className="mt-2 max-h-56 overflow-y-auto overscroll-contain rounded-lg border border-slate-100">
        {err ? <p className="px-3 py-4 text-[12.5px] text-rose-600">{err}</p>
          : !list ? <div className="p-3"><div className="skeleton h-6 w-full" /></div>
            : matches.length === 0 ? <p className="px-3 py-4 text-[12.5px] text-slate-400">No participant matches that search.</p>
              : (
                <ul className="divide-y divide-slate-100">
                  {matches.map((c) => (
                    <li key={c.id}>
                      <button
                        type="button" disabled={busy}
                        onClick={() => onPick && onPick(c.id, c.name)}
                        className="flex w-full items-center gap-2 px-3 py-2 text-left transition-colors motion-reduce:transition-none hover:bg-slate-50 disabled:opacity-40
                                   focus-visible:bg-primary-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-400">
                        <span className="min-w-0 flex-1 truncate text-[13px] text-slate-800">{c.name}</span>
                        <span className="shrink-0 tabular-nums text-[11.5px] text-slate-400">{c.ndis_number || '—'}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
      </div>
    </div>
  );
}

function IntakeRecord({ item, loading, busy, onLink, onRescan, onNotInvoice }) {
  const [noteOpen, setNoteOpen] = useState(false);
  const [note, setNote] = useState('');
  const itemId = item ? item.id : null;
  // A different item means a different decision — never carry a half-typed note across records.
  useEffect(() => { setNoteOpen(false); setNote(''); }, [itemId]);

  if (!item) return null;
  const hold = item.hold_reason ? holdInfo(item.hold_reason) : null;
  const fields = extractionRows(item.extraction, item.confidence, item.low_confidence_fields);
  const suggestions = Array.isArray(item.suggestions) ? item.suggestions : [];
  const held = item.state === 'received';

  return (
    <div className="flex min-h-full flex-col">
      <header className="px-5 pb-4 pt-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className={clsx('badge', held ? (hold ? 'badge-yellow' : 'badge-green') : 'badge-gray')}>
            {held ? (hold ? 'Held for review' : 'Matched — ready to process') : humanize(item.state)}
          </span>
          {item.match_confirmed && <span className="badge badge-green">Participant confirmed</span>}
          {item.match_method && <span className="badge badge-gray">Matched by {item.match_method}</span>}
          {loading && <span className="text-[11px] text-slate-400">Refreshing…</span>}
        </div>
        <h2 className="mt-1.5 text-[19px] font-semibold leading-tight text-slate-900">
          {item.subject || 'No subject'}
        </h2>
        <p className="mt-1 flex items-center gap-1.5 text-[12.5px] text-slate-500">
          <Mail size={12} className="shrink-0 text-slate-400" />
          <span className="truncate">{item.sender_name || 'Unknown sender'}</span>
        </p>
      </header>

      {/* 3 — why it is held, before anything else. BlockReasonList is the module's one shape for
          "this cannot move forward", so intake and invoices read the same way. */}
      {held && (
        <Section title="Why it is held">
          <BlockReasonList
            title={hold ? `Held: ${hold.label}` : undefined}
            reasons={hold ? [{ code: item.hold_reason, label: hold.label, detail: item.hold_detail, nextStep: hold.nextStep, severity: hold.tone === 'rose' ? 'block' : 'warn' }] : []}
            emptyLabel="Nothing is holding this item — the participant is confirmed and it can be processed."
            onRecheck={onRescan ? () => onRescan() : undefined}
            recheckLabel="Rescan"
          />
        </Section>
      )}

      {/* 5 — suggestions, kept next to the reason they exist */}
      {held && (suggestions.length > 0 || !item.match_confirmed) && (
        <Section title="Participant">
          {item.client_id && (
            <div className="mb-3 flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-2">
              <CheckCircle2 size={14} className="shrink-0 text-emerald-600" />
              <p className="min-w-0 text-[12.5px] text-emerald-800">
                Linked to <span className="font-semibold">{item.participant || 'a participant'}</span>
              </p>
            </div>
          )}
          <SuggestionList suggestions={suggestions} onLink={onLink} busy={busy} />
          <div className="mt-3">
            <ParticipantPicker onPick={onLink} busy={busy} />
          </div>
        </Section>
      )}

      {/* 1 — the email trail, verbatim from spec 5.1 */}
      <Section title="Email trail">
        <dl className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
          <Meta label="Sender name" value={item.sender_name} />
          <Meta label="Sender address" value={item.sender_address} copy />
          <Meta label="Subject" value={item.subject} className="sm:col-span-2" />
          <Meta label="Received" value={fmtDateTime(item.received_at)} />
          <Meta label="Mailbox label" value={item.mailbox_label} />
          <Meta label="Message ID" value={item.message_id} mono copy className="sm:col-span-2" />
        </dl>
      </Section>

      {/* 2 — the attachment, with the hash an operator quotes when a provider disputes a resend */}
      <Section title="Attachment">
        <div className="rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3">
          <div className="flex items-start gap-2.5">
            <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white text-slate-400 ring-1 ring-slate-200">
              <Paperclip size={15} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[13px] font-medium text-slate-900">{item.filename || 'No file name'}</p>
              <p className="mt-0.5 text-[11.5px] text-slate-500">
                {item.content_type || 'Unknown type'} · <span className="tabular-nums">{fmtBytes(item.size_bytes)}</span>
              </p>
            </div>
          </div>
          <dl className="mt-3 border-t border-slate-200 pt-3">
            <Meta label="SHA-256" value={item.sha256} mono copy />
          </dl>
        </div>
      </Section>

      {/* 4 — what was extracted, with the flagged fields impossible to miss */}
      <Section
        title="What was extracted"
        right={Array.isArray(item.low_confidence_fields) && item.low_confidence_fields.length > 0 ? (
          <span className="badge badge-yellow">{item.low_confidence_fields.length} flagged</span>
        ) : null}>
        {fields.length === 0
          ? <p className="py-2 text-[12.5px] text-slate-400">Nothing was extracted from this attachment.</p>
          : (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {fields.map((f) => <ExtractedField key={f.key} field={f} />)}
            </div>
          )}
      </Section>

      <Section title="Trail">
        <Timeline
          dense
          items={[
            { id: 'received', actor: item.sender_name || 'Mailbox', action: 'delivered this attachment', at: item.received_at, tone: 'slate' },
            { id: 'captured', action: 'Captured into the holding queue', at: item.created_at, tone: 'primary' },
            item.resolved_at && { id: 'resolved', action: `Marked ${humanize(item.state)}`, at: item.resolved_at, note: item.note, tone: 'emerald' },
          ].filter(Boolean)}
        />
      </Section>

      <div className="flex-1" />

      {held && (
        <footer className="sticky bottom-0 shrink-0 border-t border-slate-200 bg-white/95 px-5 py-3 backdrop-blur-sm">
          {noteOpen ? (
            <div className="space-y-2">
              <label htmlFor="pmq-not-invoice-note" className="text-[11px] font-semibold uppercase tracking-[0.06em] text-slate-500">
                Why is this not an invoice?
              </label>
              <textarea
                id="pmq-not-invoice-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="Statement, remittance, marketing or a query — the email is kept either way."
                className="input-field !text-[13px]"
              />
              <div className="flex items-center gap-2">
                <button type="button" className="btn-secondary btn-sm" onClick={() => setNoteOpen(false)} disabled={busy}>Cancel</button>
                <button type="button" className="btn-primary btn-sm ml-auto" disabled={busy}
                  onClick={() => onNotInvoice && onNotInvoice(note.trim())}>
                  <Check size={12} />Mark not an invoice
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <button type="button" className="btn-secondary btn-sm" onClick={() => onRescan && onRescan()} disabled={busy}>
                <RefreshCw size={12} />Rescan
              </button>
              <button type="button" className="btn-ghost btn-sm" onClick={() => setNoteOpen(true)} disabled={busy}>
                Not an invoice
              </button>
            </div>
          )}
        </footer>
      )}
    </div>
  );
}

function IntakeQueue({ onCount, offset }) {
  const [state, setState] = useState('received');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reason, setReason] = useState(null);       // single-select hold-reason chip
  const [q, setQ] = useState('');
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const reqRef = useRef(0);

  const load = useCallback(async () => {
    const seq = ++reqRef.current;
    setLoading(true); setError(null);
    try {
      const r = await api.get(`${PM}/intake/queue`, { params: { state } });
      if (seq !== reqRef.current) return;
      setData(r.data || {});
    } catch (e) {
      if (seq !== reqRef.current) return;
      setError({ message: apiMessage(e, 'Could not load the intake queue.'), forbidden: isForbidden(e) });
    } finally {
      if (seq === reqRef.current) setLoading(false);
    }
  }, [state]);

  useEffect(() => { load(); }, [load]);

  const items = useMemo(() => (data && Array.isArray(data.items) ? data.items : []), [data]);
  useEffect(() => {
    // Only the received queue is a workload; the other states are archives.
    if (onCount && state === 'received' && data) onCount(items.length);
  }, [onCount, state, data, items.length]);

  // The API returns the whole state in one call (capped at 500) and takes no reason or search
  // parameter, so narrowing happens here rather than costing a round trip per chip.
  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return items.filter((it) => {
      if (reason === READY && it.hold_reason) return false;
      if (reason && reason !== READY && it.hold_reason !== reason) return false;
      if (!needle) return true;
      return `${it.sender_name || ''} ${it.sender_address || ''} ${it.subject || ''} ${it.filename || ''} ${it.participant || ''}`
        .toLowerCase().includes(needle);
    });
  }, [items, reason, q]);

  const stateChips = useMemo(() => {
    const counts = (data && data.counts) || {};
    return INTAKE_STATES.map((s) => ({ value: s.value, label: s.label, count: counts[s.value] }));
  }, [data]);

  const reasonChips = useMemo(() => {
    const byReason = (data && Array.isArray(data.by_reason) ? data.by_reason : []);
    const ready = items.filter((i) => !i.hold_reason).length;
    return [
      { value: ALL, label: 'All', count: items.length },
      ...byReason.map((r) => {
        const info = holdInfo(r.reason);
        return { value: r.reason, label: info.label, count: r.count, tone: info.tone };
      }),
      ready > 0 ? { value: READY, label: 'Ready to process', count: ready, tone: 'emerald' } : null,
    ].filter(Boolean);
  }, [data, items]);

  // Open instantly from the list row, then replace it with the server's copy so the trail on
  // screen is always the current one.
  const open = useCallback(async (row) => {
    setOpenId(row.id); setDetail(row); setDetailLoading(true);
    try {
      const r = await api.get(`${PM}/intake/queue/${row.id}`);
      setDetail((cur) => (cur && cur.id === row.id ? r.data : cur));
    } catch (e) {
      toast.error(apiMessage(e, 'Could not refresh this item.'));
    } finally { setDetailLoading(false); }
  }, []);

  // The mutating routes all return the updated row, so the list and the open record are patched
  // from the response instead of being refetched — the operator keeps their scroll position.
  const patchRow = useCallback((row) => {
    setDetail((cur) => (cur && cur.id === row.id ? row : cur));
    setData((cur) => (cur ? { ...cur, items: (cur.items || []).map((i) => (i.id === row.id ? row : i)) } : cur));
  }, []);

  const dropRow = useCallback((id) => {
    setDetail(null); setOpenId(null);
    setData((cur) => (cur ? { ...cur, items: (cur.items || []).filter((i) => i.id !== id) } : cur));
  }, []);

  const link = useCallback(async (clientId, name) => {
    if (!openId) return;
    setBusy(true);
    try {
      const r = await api.post(`${PM}/intake/queue/${openId}/link`, { client_id: clientId });
      patchRow(r.data);
      toast.success(`Linked to ${name || 'the participant'}`);
    } catch (e) { toast.error(apiMessage(e, 'Could not link that participant.')); } finally { setBusy(false); }
  }, [openId, patchRow]);

  const rescan = useCallback(async (id) => {
    const target = id || openId;
    if (!target) return;
    setBusy(true);
    try {
      const r = await api.post(`${PM}/intake/queue/${target}/rescan`);
      patchRow(r.data);
      toast.success(r.data && r.data.hold_reason
        ? `Still held — ${holdInfo(r.data.hold_reason).label}`
        : 'Matched. This item can now be processed.');
    } catch (e) { toast.error(apiMessage(e, 'Rescan failed.')); } finally { setBusy(false); }
  }, [openId, patchRow]);

  const rescanAll = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.post(`${PM}/intake/rescan-all`);
      const d = r.data || {};
      toast.success(`Rescanned ${d.scanned || 0} — ${d.now_matched || 0} now match`);
      await load();
    } catch (e) { toast.error(apiMessage(e, 'Rescan failed.')); } finally { setBusy(false); }
  }, [load]);

  const notAnInvoice = useCallback(async (note) => {
    if (!openId) return;
    setBusy(true);
    try {
      await api.post(`${PM}/intake/queue/${openId}/not-an-invoice`, { note: note || null });
      dropRow(openId);
      toast.success('Marked not an invoice. The email is kept on record.');
    } catch (e) { toast.error(apiMessage(e, 'Could not update that item.')); } finally { setBusy(false); }
  }, [openId, dropRow]);

  const columns = useMemo(() => [
    {
      key: 'subject', header: 'Email', sortable: true, truncate: true, width: '32%',
      render: (r) => (
        <div className="min-w-0">
          <div className="flex min-w-0 items-center gap-1.5">
            <span className="truncate font-medium text-slate-900">{r.subject || 'No subject'}</span>
            {r.filename && <Paperclip size={11} className="shrink-0 text-slate-300" aria-label="Attachment on file" />}
          </div>
          <span className="block max-w-full truncate text-[11.5px] text-slate-500">
            {r.sender_name || r.sender_address || 'Unknown sender'}
          </span>
        </div>
      ),
    },
    {
      key: 'participant', header: 'Participant', sortable: true, truncate: true, width: '20%',
      render: (r) => (r.participant
        ? (
          <span className="flex min-w-0 items-center gap-1.5">
            <span className="truncate text-slate-700">{r.participant}</span>
            {r.match_confirmed && <Check size={11} className="shrink-0 text-emerald-500" aria-label="Confirmed" />}
          </span>
        )
        : <span className="text-slate-300">Unmatched</span>),
    },
    {
      key: 'hold_reason', header: 'Held for', sortable: true, width: '24%',
      render: (r) => {
        if (!r.hold_reason) return <span className="badge badge-green">Ready</span>;
        const info = holdInfo(r.hold_reason);
        return <span className={clsx('badge', info.tone === 'rose' ? 'badge-red' : info.tone === 'amber' ? 'badge-yellow' : 'badge-gray')}>{info.label}</span>;
      },
    },
    {
      key: 'received_at', header: 'Received', sortable: true, align: 'right', width: '14%',
      className: 'hidden sm:table-cell', headerClassName: 'hidden sm:table-cell',
      render: (r) => <span className="tabular-nums text-slate-500">{fmtDate(r.received_at || r.created_at)}</span>,
    },
    {
      key: 'size_bytes', header: 'Size', align: 'right', sortable: true, width: '10%',
      className: 'hidden lg:table-cell', headerClassName: 'hidden lg:table-cell',
      render: (r) => <span className="tabular-nums text-slate-400">{fmtBytes(r.size_bytes)}</span>,
    },
  ], []);

  // Accent only in the live queue — in the archive states every row would be green, which is noise.
  const rowTone = useCallback((r) => {
    if (state !== 'received') return null;
    return r.hold_reason ? holdInfo(r.hold_reason).tone : 'emerald';
  }, [state]);

  if (error && error.forbidden) return <AccessDenied message={error.message} />;

  return (
    <Workbench detailOpen={!!openId} onCloseDetail={() => { setOpenId(null); setDetail(null); }} offset={offset}>
      <WorkbenchList width="wide" scroll={false} label="Intake queue">
        <Toolbar
          title="Received holding queue" count={rows.length} countLabel="items" icon={Inbox}
          search={q} onSearch={setQ} searchPlaceholder="Sender, subject, file or participant" searchShortcut="/"
          actions={(
            <>
              <button type="button" className="btn-secondary btn-sm" onClick={load} disabled={loading}>
                <RefreshCw size={12} className={clsx(loading && 'animate-spin motion-reduce:animate-none')} />Refresh
              </button>
              <button type="button" className="btn-primary btn-sm" onClick={rescanAll} disabled={busy || loading}>
                <RefreshCw size={12} />Rescan all
              </button>
            </>
          )}
          chips={(
            <div className="flex w-full flex-col gap-2">
              <FilterChips options={stateChips} value={state} onChange={(v) => { setState(v || 'received'); setReason(null); setOpenId(null); setDetail(null); }}
                multi={false} ariaLabel="Intake state" />
              {state === 'received' && reasonChips.length > 1 && (
                <FilterChips options={reasonChips} value={reason} onChange={(v) => setReason(noneIfAll(v))} multi={false} ariaLabel="Filter by hold reason" />
              )}
            </div>
          )}
        />

        <DataTable
          label="Intake queue" className="flex-1 min-h-0" maxHeight="100%"
          columns={columns} rows={rows} rowKey="id"
          loading={loading} error={error ? error.message : null} onRetry={load}
          empty={{
            icon: FileSearch,
            title: state === 'received' ? 'Nothing is waiting for review' : 'Nothing in this state',
            hint: q || reason
              ? 'No item matches that filter. Clear it to see the whole queue.'
              : 'Every email that arrived has been matched and processed.',
          }}
          defaultSort={{ key: 'received_at', dir: 'desc' }}
          onRowClick={open} activeRowKey={openId} rowTone={rowTone}
        />
      </WorkbenchList>

      <WorkbenchDetail
        label="Intake item" backLabel="Back to queue"
        placeholder={(
          <EmptyState
            icon={Mail}
            title="Select an item"
            hint="The email trail, the attachment and its hash, why the item is held and what the extractor read all appear here — the queue stays where it is."
          />
        )}>
        <IntakeRecord
          item={detail} loading={detailLoading} busy={busy}
          onLink={link} onRescan={() => rescan()} onNotInvoice={notAnInvoice}
        />
      </WorkbenchDetail>
    </Workbench>
  );
}

/* ═════════════════════════════════════════════════════════════════════════════
   TAB 2 — NOTIFICATIONS (spec 13)
   ═════════════════════════════════════════════════════════════════════════════ */

const AUDIENCES = [
  { value: 'plan_management', label: 'Plan management' },
  { value: 'finance', label: 'Finance' },
  { value: 'administrator', label: 'Administrator' },
];
const AUDIENCE_LABEL = AUDIENCES.reduce((a, x) => { a[x.value] = x.label; return a; }, {});

// The backend's `ageing` bucket, verbatim. It is derived in SQL from due_at, so the chip and the
// row can never disagree about whether a task is late.
const AGEING = {
  overdue: { label: 'Overdue', badge: 'badge-red', tone: 'rose' },
  due_today: { label: 'Due today', badge: 'badge-yellow', tone: 'amber' },
  on_track: { label: 'Within target', badge: 'badge-green', tone: 'emerald' },
  no_target: { label: 'No target', badge: 'badge-gray', tone: 'slate' },
  resolved: { label: 'Resolved', badge: 'badge-gray', tone: 'slate' },
};
const ageInfo = (a) => AGEING[a] || AGEING.no_target;

const ENTITY_LABEL = {
  invoice: 'Invoice', provider: 'Provider', participant: 'Participant',
  claim: 'Claim batch', payment_batch: 'Payment batch', plan: 'Plan',
};

// The API's third status IS called 'all', which is the one value FilterChips reserves for its
// reset. The chip carries 'any' and is translated on the way out, so the chip highlights correctly
// and the API still receives the word it expects.
const STATUS_CHIPS = [
  { value: 'open', label: 'Open' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'any', label: 'All' },
];
const statusParam = (s) => (s === 'any' ? 'all' : s);

// Roles that can act on a plan-management task. The API only checks the user is in the org, so
// narrowing here stops a finance chase landing on a support worker.
const ASSIGNABLE_ROLES = ['admin', 'office_user'];

function AgeingChip({ row }) {
  const info = ageInfo(row.ageing);
  const late = row.ageing === 'overdue';
  return (
    <span className="flex min-w-0 flex-col items-start gap-0.5">
      <span className={clsx('badge', info.badge, late && 'font-bold')}>
        {late && <AlertTriangle size={10} className="shrink-0" />}
        {info.label}
        {late && row.overdue_business_days > 0 && (
          <span className="tabular-nums">{row.overdue_business_days}d</span>
        )}
      </span>
      {row.target_label && (
        <span className="truncate text-[10.5px] text-slate-400">{row.target_label}</span>
      )}
    </span>
  );
}

const assigneeName = (r) => {
  const n = `${r.assignee_first_name || ''} ${r.assignee_last_name || ''}`.trim();
  return n || null;
};

function NotificationRecord({ row, busy, staff, staffError, onLoadStaff, onResolve, onAssign, onOpenLink }) {
  const [note, setNote] = useState('');
  const rowId = row ? row.id : null;
  useEffect(() => { setNote(''); }, [rowId]);
  // The assignee list is only worth fetching once a record is actually open.
  useEffect(() => { if (rowId && onLoadStaff) onLoadStaff(); }, [rowId, onLoadStaff]);

  if (!row) return null;
  const info = ageInfo(row.ageing);
  const resolved = !!row.resolved_at;
  const assignable = (staff || []).filter((s) => ASSIGNABLE_ROLES.includes(s.role));

  return (
    <div className="flex min-h-full flex-col">
      <header className="px-5 pb-4 pt-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className={clsx('badge', row.severity === 'high' ? 'badge-red' : row.severity === 'medium' ? 'badge-yellow' : 'badge-gray')}>
            {humanize(row.severity || 'low')} priority
          </span>
          <span className="badge badge-gray">{AUDIENCE_LABEL[row.assigned_role] || humanize(row.assigned_role)}</span>
          {row.spec && <span className="badge badge-gray">Spec {row.spec}</span>}
        </div>
        <h2 className="mt-1.5 text-[19px] font-semibold leading-tight text-slate-900">{row.label || humanize(row.type)}</h2>
        <p className="mt-1 text-[13px] text-slate-600">{row.title}</p>
      </header>

      {/* Where it sits against the spec-13 response target — the reason this queue exists. */}
      <Section title="Response target">
        <div className={clsx('rounded-xl border px-4 py-3',
          row.ageing === 'overdue' ? 'border-rose-200 bg-rose-50/60'
            : row.ageing === 'due_today' ? 'border-amber-200 bg-amber-50/60'
              : 'border-slate-200 bg-slate-50/60')}>
          <div className="flex items-start gap-2.5">
            <Clock size={15} className={clsx('mt-0.5 shrink-0',
              row.ageing === 'overdue' ? 'text-rose-600' : row.ageing === 'due_today' ? 'text-amber-600' : 'text-slate-400')} />
            <div className="min-w-0 flex-1">
              <p className={clsx('text-[13px] font-semibold',
                row.ageing === 'overdue' ? 'text-rose-700' : row.ageing === 'due_today' ? 'text-amber-700' : 'text-slate-700')}>
                {info.label}
                {row.ageing === 'overdue' && row.overdue_business_days > 0 && (
                  <span className="tabular-nums"> — {row.overdue_business_days} business {row.overdue_business_days === 1 ? 'day' : 'days'} late</span>
                )}
              </p>
              {row.target_label && (
                <p className="mt-0.5 text-[12px] text-slate-600">
                  Target: <span className="font-medium">{row.target_label}</span>
                  {row.due_at ? <> · due {fmtDateTime(row.due_at)}</> : null}
                </p>
              )}
            </div>
          </div>
        </div>
      </Section>

      {row.body && (
        <Section title="What happened, and what to do">
          <p className="text-[13px] leading-relaxed text-slate-700">{row.body}</p>
        </Section>
      )}

      <Section title="Linked record">
        <div className="flex items-center gap-2.5 rounded-xl border border-slate-200 bg-white px-4 py-3">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-50 text-slate-400">
            <FileText size={15} />
          </span>
          <div className="min-w-0 flex-1">
            <MicroLabel>{ENTITY_LABEL[row.entity] || humanize(row.entity)}</MicroLabel>
            <p className="mt-0.5 flex items-center gap-1 truncate text-[12px] text-slate-500">
              <span className="truncate font-mono text-[11px]">{row.entity_id || 'No record id'}</span>
              {row.entity_id && <CopyButton value={row.entity_id} size={10} />}
            </p>
          </div>
          {row.link && (
            <button type="button" className="btn-secondary btn-sm shrink-0" onClick={() => onOpenLink && onOpenLink(row.link)}>
              <ExternalLink size={12} />Open
            </button>
          )}
        </div>
      </Section>

      <Section title="Owner">
        <div className="flex flex-wrap items-center gap-2">
          <span className="badge badge-gray">{AUDIENCE_LABEL[row.assigned_role] || humanize(row.assigned_role)}</span>
          <label htmlFor="pmq-assign" className="sr-only">Assign to</label>
          <select
            id="pmq-assign" className="input-field !h-8 !w-auto !py-0 !text-[13px]" disabled={busy || resolved}
            value={row.assigned_user || ''}
            onChange={(e) => onAssign && onAssign(row, e.target.value || null)}>
            <option value="">Unassigned</option>
            {assignable.map((s) => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
          </select>
          {staffError && <span className="text-[11.5px] text-slate-400">{staffError}</span>}
        </div>
      </Section>

      <Section title="Trail">
        <Timeline
          dense
          items={[
            { id: 'raised', action: 'Raised', at: row.created_at, tone: 'amber' },
            row.due_at && { id: 'due', action: `Response target — ${row.target_label || 'due'}`, at: row.due_at, tone: row.ageing === 'overdue' ? 'rose' : 'slate' },
            row.resolved_at && { id: 'resolved', action: 'Resolved', at: row.resolved_at, note: row.resolution_note, tone: 'emerald' },
          ].filter(Boolean)}
        />
      </Section>

      <div className="flex-1" />

      <footer className="sticky bottom-0 shrink-0 border-t border-slate-200 bg-white/95 px-5 py-3 backdrop-blur-sm">
        {resolved ? (
          <div className="flex items-start gap-2">
            <CheckCircle2 size={15} className="mt-0.5 shrink-0 text-emerald-600" />
            <div className="min-w-0">
              <p className="text-[12.5px] font-semibold text-emerald-700">Resolved {fmtDateTime(row.resolved_at)}</p>
              {row.resolution_note && <p className="mt-0.5 text-[12px] text-slate-600">{row.resolution_note}</p>}
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <label htmlFor="pmq-resolve-note" className="text-[11px] font-semibold uppercase tracking-[0.06em] text-slate-500">
              Resolution note
            </label>
            <textarea
              id="pmq-resolve-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="What was done. Closing the task is the record that the work happened."
              className="input-field !text-[13px]"
            />
            <div className="flex justify-end">
              <button type="button" className="btn-primary btn-sm" disabled={busy}
                onClick={() => onResolve && onResolve(row, note.trim())}>
                <Check size={12} />Resolve
              </button>
            </div>
          </div>
        )}
      </footer>
    </div>
  );
}

function NotificationQueue({ onCount, offset }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState('open');
  const [audience, setAudience] = useState(null);
  const [ageing, setAgeing] = useState(null);
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [selected, setSelected] = useState([]);
  const [bulkNote, setBulkNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [staff, setStaff] = useState(null);
  const [staffError, setStaffError] = useState(null);
  const reqRef = useRef(0);

  const load = useCallback(async () => {
    const seq = ++reqRef.current;
    setLoading(true); setError(null);
    try {
      const r = await api.get(`${PM}/notifications`, {
        params: {
          status: statusParam(status), audience: audience || undefined,
          ageing: ageing || undefined, q: q.trim() || undefined, limit: 300,
        },
      });
      if (seq !== reqRef.current) return;
      setData(r.data || {});
    } catch (e) {
      if (seq !== reqRef.current) return;
      setError({ message: apiMessage(e, 'Could not load the notification queue.'), forbidden: isForbidden(e) });
    } finally {
      if (seq === reqRef.current) setLoading(false);
    }
  }, [status, audience, ageing, q]);

  // Debounced so typing in the search box does not fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(load, q ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, q]);

  const rows = useMemo(() => (data && Array.isArray(data.notifications) ? data.notifications : []), [data]);
  const counts = (data && data.counts) || {};
  useEffect(() => { if (onCount && status === 'open' && data) onCount(counts.total); }, [onCount, status, data, counts.total]);

  const loadStaff = useCallback(() => {
    if (staff || staffError) return;
    api.get('/staff')
      .then((r) => setStaff(Array.isArray(r.data) ? r.data : []))
      .catch((e) => setStaffError(apiMessage(e, 'Assignee list unavailable.')));
  }, [staff, staffError]);

  const openRow = useMemo(() => rows.find((r) => r.id === openId) || null, [rows, openId]);

  const patchRow = useCallback((row) => {
    setData((cur) => (cur ? { ...cur, notifications: (cur.notifications || []).map((n) => (n.id === row.id ? row : n)) } : cur));
  }, []);

  const resolve = useCallback(async (row, note) => {
    setBusy(true);
    try {
      const r = await api.patch(`${PM}/notifications/${row.id}/resolve`, { note: note || null });
      // While the queue is filtered to Open, a resolved row no longer belongs in it.
      if (status === 'open') {
        setData((cur) => (cur ? { ...cur, notifications: (cur.notifications || []).filter((n) => n.id !== row.id) } : cur));
        setOpenId((cur) => (cur === row.id ? null : cur));
      } else patchRow(r.data);
      setSelected((cur) => cur.filter((id) => id !== row.id));
      toast.success('Resolved');
    } catch (e) { toast.error(apiMessage(e, 'Could not resolve that task.')); } finally { setBusy(false); }
  }, [status, patchRow]);

  const bulkResolve = useCallback(async () => {
    if (!selected.length) return;
    setBusy(true);
    const note = bulkNote.trim() || null;
    const results = await Promise.allSettled(selected.map((id) => api.patch(`${PM}/notifications/${id}/resolve`, { note })));
    const ok = results.filter((r) => r.status === 'fulfilled').length;
    const failed = results.length - ok;
    setBusy(false); setBulkNote(''); setSelected([]);
    if (ok) toast.success(`Resolved ${ok} ${ok === 1 ? 'task' : 'tasks'}`);
    if (failed) toast.error(`${failed} could not be resolved`);
    load();
  }, [selected, bulkNote, load]);

  const assign = useCallback(async (row, userId) => {
    setBusy(true);
    try {
      const r = await api.patch(`${PM}/notifications/${row.id}/assign`, { user_id: userId });
      // The assign response carries no joined assignee name, so it is filled from the staff list
      // to stop the row flickering back to "Unassigned".
      const person = (staff || []).find((s) => s.id === userId);
      patchRow({
        ...r.data,
        assignee_first_name: person ? person.first_name : null,
        assignee_last_name: person ? person.last_name : null,
      });
      toast.success(userId ? `Assigned to ${person ? `${person.first_name} ${person.last_name}` : 'that user'}` : 'Unassigned');
    } catch (e) { toast.error(apiMessage(e, 'Could not assign that task.')); } finally { setBusy(false); }
  }, [staff, patchRow]);

  const columns = useMemo(() => [
    {
      key: 'label', header: 'Task', sortable: true, truncate: true, width: '38%',
      render: (r) => (
        <div className="min-w-0">
          <span className="block truncate font-medium text-slate-900">{r.label || humanize(r.type)}</span>
          <span className="block max-w-full truncate text-[11.5px] text-slate-500">{r.title}</span>
        </div>
      ),
    },
    {
      key: 'entity', header: 'Record', width: '16%', truncate: true,
      className: 'hidden md:table-cell', headerClassName: 'hidden md:table-cell',
      render: (r) => (r.link ? (
        <button
          type="button" onClick={(e) => { e.stopPropagation(); navigate(r.link); }}
          className="max-w-full truncate text-[12.5px] text-slate-600 underline-offset-2 hover:text-primary-600 hover:underline
                     focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 rounded">
          {ENTITY_LABEL[r.entity] || humanize(r.entity)}
        </button>
      ) : <span className="text-slate-400">{ENTITY_LABEL[r.entity] || humanize(r.entity)}</span>),
    },
    {
      key: 'created_at', header: 'Raised', sortable: true, align: 'right', width: '14%',
      className: 'hidden sm:table-cell', headerClassName: 'hidden sm:table-cell',
      render: (r) => (
        <span className="tabular-nums text-slate-500" title={fmtDateTime(r.created_at)}>
          {fmtDate(r.created_at)}
        </span>
      ),
    },
    {
      key: 'ageing', header: 'Target', sortable: true, width: '18%',
      render: (r) => <AgeingChip row={r} />,
    },
    {
      key: 'assigned_user', header: 'Assignee', truncate: true, width: '14%',
      className: 'hidden lg:table-cell', headerClassName: 'hidden lg:table-cell',
      render: (r) => (assigneeName(r)
        ? <span className="truncate text-slate-600">{assigneeName(r)}</span>
        : <span className="text-slate-300">Unassigned</span>),
    },
  ], [navigate]);

  const rowTone = useCallback((r) => (r.ageing === 'overdue' ? 'rose' : r.ageing === 'due_today' ? 'amber' : null), []);

  // Spec 13 owns the work by audience, so the list is grouped that way even when the chips have
  // narrowed it — a mixed flat list hides who is meant to act.
  const groups = useMemo(() => {
    const order = AUDIENCES.map((a) => a.value);
    const buckets = new Map();
    for (const r of rows) {
      const k = r.assigned_role || 'unassigned';
      if (!buckets.has(k)) buckets.set(k, []);
      buckets.get(k).push(r);
    }
    return [...buckets.entries()]
      .sort((a, b) => {
        const ia = order.indexOf(a[0]); const ib = order.indexOf(b[0]);
        return (ia === -1 ? order.length : ia) - (ib === -1 ? order.length : ib);
      })
      .map(([key, list]) => ({
        key,
        label: AUDIENCE_LABEL[key] || humanize(key),
        rows: list,
        ids: new Set(list.map((r) => r.id)),
        overdue: list.filter((r) => r.ageing === 'overdue').length,
      }));
  }, [rows]);

  // One selection spans every group, but each DataTable only ever reports on its OWN rows —
  // its header "select all" hands back just that group's keys, which would otherwise wipe what is
  // ticked in the other two audiences.
  const selectGroup = useCallback((group, next) => {
    setSelected((cur) => [...cur.filter((id) => !group.ids.has(id)), ...next]);
  }, []);

  const statusChips = useMemo(() => STATUS_CHIPS.map((s) => ({ ...s, count: s.value === status ? counts.total : undefined })), [status, counts.total]);
  const audienceChips = useMemo(() => ([
    { value: ALL, label: 'All owners' },
    ...AUDIENCES.map((a) => ({ ...a, count: (counts.by_audience || {})[a.value] })),
  ]), [counts.by_audience]);
  const ageingChips = useMemo(() => {
    const by = counts.by_ageing || {};
    return [
      { value: ALL, label: 'Any target' },
      { value: 'overdue', label: 'Overdue', count: by.overdue, tone: 'rose' },
      { value: 'due_today', label: 'Due today', count: by.due_today, tone: 'amber' },
      { value: 'on_track', label: 'Within target', count: by.on_track, tone: 'emerald' },
    ];
  }, [counts.by_ageing]);

  if (error && error.forbidden) return <AccessDenied message={error.message} />;

  const listState = loading ? 'loading' : error ? 'error' : rows.length === 0 ? 'empty' : 'rows';

  return (
    <Workbench detailOpen={!!openId} onCloseDetail={() => setOpenId(null)} offset={offset}>
      <WorkbenchList width="wide" label="Notification queue">
        <Toolbar
          title="Tasks" count={counts.total} countLabel="tasks" icon={Bell}
          search={q} onSearch={setQ} searchPlaceholder="Search titles and detail" searchShortcut="/"
          actions={(
            <button type="button" className="btn-secondary btn-sm" onClick={load} disabled={loading}>
              <RefreshCw size={12} className={clsx(loading && 'animate-spin motion-reduce:animate-none')} />Refresh
            </button>
          )}
          chips={(
            <div className="flex w-full flex-col gap-2">
              <div className="flex flex-wrap items-center gap-2">
                <FilterChips options={statusChips} value={status} onChange={(v) => setStatus(v || 'open')} multi={false} ariaLabel="Task status" />
                <span className="h-4 w-px bg-slate-200" aria-hidden />
                <FilterChips options={ageingChips} value={ageing} onChange={(v) => setAgeing(noneIfAll(v))} multi={false} ariaLabel="Filter by response target" />
              </div>
              <FilterChips options={audienceChips} value={audience} onChange={(v) => setAudience(noneIfAll(v))} multi={false} ariaLabel="Filter by owner" />
            </div>
          )}
        />

        {listState === 'loading' && <TableSkeleton rows={8} cols={5} />}
        {listState === 'error' && <ErrorState message={error.message} onRetry={load} />}
        {listState === 'empty' && (
          <EmptyState
            icon={CheckCircle2}
            title={status === 'open' ? 'Nothing is waiting on anyone' : 'No tasks match these filters'}
            hint={q || audience || ageing
              ? 'No task matches those filters. Clear them to see the whole queue.'
              : 'Every plan management, finance and administrator task has been resolved.'}
          />
        )}
        {listState === 'rows' && groups.map((g) => (
          <div key={g.key} className="border-b border-slate-100 last:border-b-0">
            <div className="flex items-center gap-2 bg-slate-50/70 px-4 py-2">
              <MicroLabel as="h3">{g.label}</MicroLabel>
              <span className="tabular-nums text-[11px] text-slate-400">{g.rows.length}</span>
              {g.overdue > 0 && (
                <span className="badge badge-red !text-[10px]">
                  <span className="tabular-nums">{g.overdue}</span> overdue
                </span>
              )}
            </div>
            <DataTable
              label={`${g.label} tasks`} columns={columns} rows={g.rows} rowKey="id"
              stickyHeader={false} dense
              selectable
              selected={selected.filter((id) => g.ids.has(id))}
              onSelectedChange={(next) => selectGroup(g, next)}
              onRowClick={(r) => setOpenId(r.id)} activeRowKey={openId} rowTone={rowTone}
            />
          </div>
        ))}

        <BulkBar count={selected.length} noun="selected" onClear={() => setSelected([])}>
          <label htmlFor="pmq-bulk-note" className="sr-only">Resolution note for the selected tasks</label>
          <input
            id="pmq-bulk-note" type="text" value={bulkNote} onChange={(e) => setBulkNote(e.target.value)}
            placeholder="Resolution note (optional)"
            className="input-field !h-8 !w-48 !py-0 !text-[12.5px] sm:!w-64"
          />
          <button type="button" className="btn-primary btn-sm" disabled={busy} onClick={bulkResolve}>
            <Check size={12} />Resolve {selected.length}
          </button>
        </BulkBar>
      </WorkbenchList>

      <WorkbenchDetail
        label="Task" backLabel="Back to queue"
        placeholder={(
          <EmptyState
            icon={Bell}
            title="Select a task"
            hint="Its record, its response target and its full trail appear here. Resolving it from this pane keeps your place in the queue."
          />
        )}>
        <NotificationRecord
          row={openRow} busy={busy} staff={staff} staffError={staffError} onLoadStaff={loadStaff}
          onResolve={resolve} onAssign={assign} onOpenLink={(link) => navigate(link)}
        />
      </WorkbenchDetail>
    </Workbench>
  );
}

/* ═════════════════════════════════════════════════════════════════════════════
   SHELL
   ═════════════════════════════════════════════════════════════════════════════ */

const TABS = [
  { id: 'intake', label: 'Intake', icon: Inbox, hint: 'Emails that could not be processed automatically' },
  { id: 'notifications', label: 'Notifications', icon: Bell, hint: 'Tasks with a response target' },
];

// `tab`/`onTabChange` let PlanManagement.jsx drive the tab from the URL; left out, the component
// keeps its own. Everything else is optional.
export default function Queues({ tab, onTabChange, offset = '14.5rem' }) {
  const [innerTab, setInnerTab] = useState('intake');
  const active = TABS.some((t) => t.id === tab) ? tab : innerTab;
  const setActive = (id) => { if (onTabChange) onTabChange(id); else setInnerTab(id); };

  const [counts, setCounts] = useState({ intake: null, notifications: null, overdue: null });
  const onIntakeCount = useCallback((n) => setCounts((c) => (c.intake === n ? c : { ...c, intake: n })), []);
  const onNotifCount = useCallback((n) => setCounts((c) => (c.notifications === n ? c : { ...c, notifications: n })), []);

  // One cheap call so the Notifications badge is honest before anyone opens that tab. A 403 here
  // is not fatal — the tab itself renders the API's own message.
  useEffect(() => {
    let alive = true;
    api.get(`${PM}/notifications/counts`)
      .then((r) => { if (alive && r.data) setCounts((c) => ({ ...c, notifications: r.data.total, overdue: r.data.overdue })); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const activeHint = (TABS.find((t) => t.id === active) || TABS[0]).hint;

  return (
    <div className="min-w-0">
      {/* The screen's name lives in the Plan Management page header; this keeps only what is
          specific to the tab you are on, and the overdue count. */}
      <header className="mb-3">
        <div className="flex flex-wrap items-center gap-3">
          <span className="sr-only">{activeHint}</span>
          {counts.overdue > 0 && (
            <span className="ml-auto flex items-center gap-1.5 rounded-lg border border-rose-200 bg-rose-50 px-2.5 py-1.5 text-[12px] font-semibold text-rose-700">
              <AlertTriangle size={13} className="shrink-0" />
              <span className="tabular-nums">{counts.overdue}</span> overdue
            </span>
          )}
        </div>

        <div className="tab-bar !mb-0 mt-3" role="tablist" aria-label="Review queues">
          {TABS.map((t) => {
            const on = active === t.id;
            const n = counts[t.id];
            return (
              <button
                key={t.id} type="button" role="tab" aria-selected={on} onClick={() => setActive(t.id)}
                className={clsx('tab-item flex items-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:rounded-t-lg', on && 'active')}>
                <t.icon size={14} className={on ? 'text-primary-500' : 'text-slate-400'} />
                {t.label}
                {n != null && (
                  <span className={clsx('rounded-full px-1.5 text-[11px] font-semibold tabular-nums',
                    on ? 'bg-primary-50 text-primary-600' : 'bg-slate-100 text-slate-500')}>
                    {Number(n).toLocaleString('en-AU')}
                  </span>
                )}
                {t.id === 'notifications' && counts.overdue > 0 && (
                  <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-rose-500" aria-label={`${counts.overdue} overdue`} />
                )}
              </button>
            );
          })}
        </div>
      </header>

      {/* Each tab owns its own fetches, so switching tabs does not leave a stale queue on screen
          and the inactive queue costs nothing. */}
      {active === 'intake'
        ? <IntakeQueue onCount={onIntakeCount} offset={offset} />
        : <NotificationQueue onCount={onNotifCount} offset={offset} />}
    </div>
  );
}

export { IntakeQueue, NotificationQueue };
